<template>
    <section>
        <Header :title="isLoading || !profile ? (listProfile ? listProfile.name : 'Profiel') : profile.name"
                show-back-button></Header>
        <v-container>
            <v-breadcrumbs :items="breadcrumbs" class="px-0"></v-breadcrumbs>
            <p v-if="isLoading" class="text-center my-7">
                <v-icon large color="primary">fal fa-spinner-third fa-spin</v-icon>
            </p>
            <v-alert v-if="notFound" color="warning">
                <v-icon class="mr-3">fal fa-exclamation-triangle</v-icon>
                <span>Het profiel kon niet worden gevonden.</span>
            </v-alert>
            <v-card
                class="profile-card profile-detail-card"
                elevation="0"
                v-if="profile"
            >
                <v-card-text class="pa-0">
                    <div class="d-flex flex-column flex-md-row">
                        <div class="order-2 order-md-1 profile-title">
                            <h2 class="text-h4 mb-3 primary--text">
                                {{ profile.name }}
                            </h2>
                            <ul class="list-unstyled details">
                                <li class="mb-2">
                                    <v-icon
                                        small
                                        color="secondary"
                                        class="mr-1 fa-fw"
                                    >
                                        far fa-map-marker-alt
                                    </v-icon>
                                    {{ profile.city }}
                                </li>
                                <li class="mb-2">
                                    <v-icon
                                        small
                                        color="secondary"
                                        class="mr-1 fa-fw"
                                    >
                                        far fa-calendar-day
                                    </v-icon>
                                    {{ profile.dateOfBirth|luxon }}
                                </li>
                                <li class="mb-2">
                                    <v-icon
                                        small
                                        color="secondary"
                                        class="mr-1 fa-fw"
                                    >
                                        far fa-envelope
                                    </v-icon>
                                    {{ profile.email }}
                                </li>
                                <li class="mb-2">
                                    <v-icon
                                        small
                                        color="secondary"
                                        class="mr-1 fa-fw"
                                    >
                                        far fa-phone
                                    </v-icon>
                                    {{ profile.phoneNumber }}
                                </li>
                                <li class="mb-2" v-if="profile.linkedInUrl">
                                    <v-icon
                                        small
                                        color="secondary"
                                        class="mr-1 fa-fw"
                                    >
                                        fab fa-linkedin
                                    </v-icon>
                                    <a :href="profile.linkedInUrl" target="_blank">{{ profile.linkedInUrl }}</a>
                                </li>
                                <li class="mb-2">
                                    <v-icon
                                        small
                                        color="secondary"
                                        class="mr-1 fa-fw"
                                    >
                                        far fa-clock
                                    </v-icon>
                                    {{ profile.createdAt|luxon }}
                                </li>
                            </ul>
                        </div>
                        <v-avatar
                            size="175"
                            class="order-1 order-md-2 ml-md-auto mb-4 mb-md-0"
                            v-if="profile.image"
                        >
                            <v-img
                                :src="profile.image.url"
                                :alt="profile.name"
                            ></v-img>
                        </v-avatar>
                    </div>
                    <v-divider class="my-4"></v-divider>
                    <v-row class="align-start my-0 pb-4">
                        <v-col class="col-12 col-md-7 py-0 mb-4 mb-md-0">
                            <v-card v-if="profile.introduction">
                                <v-card-title>Introductietekst</v-card-title>
                                <v-card-text>
                                    <p>{{ profile.introduction }}</p>
                                </v-card-text>
                            </v-card>
                            <v-card v-if="profile.summary" class="mt-5">
                                <v-card-title>Vaardigheden / kwaliteiten</v-card-title>
                                <v-card-text>
                                    <p>{{ profile.summary }}</p>
                                </v-card-text>
                            </v-card>
                            <v-card v-if="profile.remarks" class="mt-5">
                                <v-card-title>Opmerkingen</v-card-title>
                                <v-card-text>
                                    <p>{{ profile.remarks }}</p>
                                </v-card-text>
                            </v-card>
                            <v-btn
                                color="primary"
                                rounded
                                large
                                elevation="0"
                                class="mt-2 mb-7"
                                v-if="profile.curriculumVitae"
                                :href="profile.curriculumVitae.url"
                                download
                                target="_blank"
                            >
                                <v-icon
                                    left
                                >
                                    fa fa-download
                                </v-icon>
                                Download CV
                            </v-btn>
                            <div v-if="profile.video" class="mb-4 mb-md-0">
                                <VimeoVideo v-if="profile.video.provider === 'vimeo'"
                                            :video-id="profile.video.id"></VimeoVideo>
                                <YoutubeVideo v-if="profile.video.provider === 'youtube'"
                                              :video-id="profile.video.id"></YoutubeVideo>
                            </div>
                        </v-col>
                        <v-col class="col-12 col-md-4 offset-md-1 py-0">
                            <v-card
                                color="#d9edf0"
                                v-for="facet in mappedFacets"
                                :key="facet.id"
                                elevation="0"
                                class="mb-3"
                            >
                                <v-card-title class="ma-0 text-h6 pb-0">{{ facet.name }}</v-card-title>
                                <v-card-text class="mx-n1">
                                    <v-chip
                                        color="primary"
                                        v-for="choice in facet.choices"
                                        :key="choice.id"
                                        class="ma-1"
                                        small
                                    >
                                        {{ choice.value }}
                                    </v-chip>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>
        </v-container>
    </section>
</template>

<script>
import Header from "@/components/layout/Header";
import {mapState} from "vuex";
import VimeoVideo from "../components/video/VimeoVideo";
import YoutubeVideo from "../components/video/YoutubeVideo";

export default {
    name: 'Profile',
    components: {
        YoutubeVideo,
        VimeoVideo,
        Header,
    },
    props: {
        uuid: {
            type: String,
            required: true,
        },
    },
    data: () => ({
        isLoading: true,
        notFound: false,
        profile: null,
        breadcrumbs: [
            {
                text: 'Dashboard',
                disabled: false,
                exact: true,
                to: {
                    name: 'Home'
                }
            },
            {
                text: 'Profielen',
                disabled: false,
                exact: true,
                to: {
                    name: 'Profiles'
                }
            },
            {
                text: 'Profiel',
                disabled: true
            },
        ],
    }),
    computed: {
        listProfile() {
            return this.profiles.find(profile => profile.uuid === this.uuid);
        },
        mappedFacets() {
            if (!this.profile) {
                return [];
            }

            let facets = [];

            this.profile.facetChoices.forEach(facetChoice => {
                let facet = facets.find(f => f.id === facetChoice.facet.id);
                if (!facet) {
                    facet = JSON.parse(JSON.stringify(facetChoice.facet));
                    facet.choices = [];
                    facets.push(facet);
                }

                facet.choices.push({
                    id: facetChoice.id,
                    value: facetChoice.value,
                    sortId: facetChoice.sortId,
                });
            });

            facets.sort((a, b) => a.sortId - b.sortId);
            facets.forEach(facet => {
                facet.choices.sort((a, b) => a.sortId - b.sortId);
            });

            return facets;
        },
        ...mapState({
            profiles: state => state.profile.profiles,
            loadedProfiles: state => state.profile.loadedProfiles,
            isLoggedIn: state => state.auth.isLoggedIn,
        }),
    },
    mounted() {
        if (this.isLoggedIn) {
            this.$store.dispatch('getProfile', this.uuid)
                .then(profile => {
                    this.profile = profile;
                })
                .catch(e => {
                    console.error(e);
                    this.$store.dispatch('showSnackbar', {color: 'error', text: 'Profiel kon niet worden opgehaald'});
                    const loadedProfile = this.loadedProfiles.find(profile => profile.uuid === this.uuid);
                    if (loadedProfile) {
                        this.profile = loadedProfile;
                    } else if (!this.listProfile) {
                        this.notFound = true;
                        this.isLoading = false;
                    }
                })
                .finally(() => {
                    if (this.profile) {
                        this.isLoading = false;
                    }
                });
        }
    },
}
</script>
